import React, { useState, memo } from 'react';
import {
  Search,
  ArrowRight,
  ShieldCheck,
  QrCode,
  Building2,
  Copy,
  CheckCircle2,
} from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';
import './repay-loan.css';
import '../styles/data-table.css';

import idfcLogo from '../assets/images/idfc_logo_for_ac.jpg';

const banks = [
  {
    name: 'IDFC First Bank',
    logo: idfcLogo,
    qr: null,
    upi: null,
    accountName: 'Marathon Finlease Pvt Ltd',
    accountNumber: '52612199561',
    ifsc: 'IDFB0020151',
    branch: 'Noida-Sector 18 Branch, Sector18 – 201301',
    accountType: 'Current Account',
  },
];

const highlights = [
  { icon: ShieldCheck, text: 'Secure bank transfer' },
  { icon: QrCode, text: 'Scan & pay via UPI' },
  { icon: CheckCircle2, text: 'Instant payment update' },
];

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    alert(`${label} copied!`);
  } catch {
    alert('Could not copy. Please copy manually.');
  }
};

const BankCard = memo(({ bank }) => (
  <div className="rl-bank-shell">
    <div className="rl-bank-panel rl-bank-panel--details">
      <div className="rl-bank-head">
        <img src={bank.logo} alt={`${bank.name} logo`} className="rl-bank-logo" />
        <div>
          <span className="rl-bank-tag">Bank transfer</span>
          <h3>Account details</h3>
        </div>
      </div>

      <dl className="rl-bank-dl">
        <div className="rl-bank-row">
          <dt>Bank name</dt>
          <dd>{bank.name}</dd>
        </div>
        <div className="rl-bank-row">
          <dt>Account holder name</dt>
          <dd>{bank.accountName}</dd>
        </div>
        <div className="rl-bank-row rl-bank-row--copy">
          <dt>Account no.</dt>
          <dd>
            {bank.accountNumber}
            <button
              type="button"
              className="rl-copy-btn"
              onClick={() => copyText(bank.accountNumber, 'Account number')}
              aria-label="Copy account number"
            >
              <Copy size={14} />
            </button>
          </dd>
        </div>
        <div className="rl-bank-row rl-bank-row--copy">
          <dt>IFSC code</dt>
          <dd>
            {bank.ifsc}
            <button
              type="button"
              className="rl-copy-btn"
              onClick={() => copyText(bank.ifsc, 'IFSC code')}
              aria-label="Copy IFSC code"
            >
              <Copy size={14} />
            </button>
          </dd>
        </div>
        <div className="rl-bank-row">
          <dt>Bank address</dt>
          <dd>{bank.branch}</dd>
        </div>
        <div className="rl-bank-row">
          <dt>Account type</dt>
          <dd>{bank.accountType}</dd>
        </div>
      </dl>
    </div>

    <div className="rl-bank-panel rl-bank-panel--qr">
      <span className="rl-bank-tag rl-bank-tag--light">UPI / QR</span>
      <h3>Scan &amp; pay</h3>
      <p className="rl-bank-qr-lead">Use any UPI app to complete your repayment instantly.</p>

      {bank.qr && (
        <div className="rl-qr-wrap">
          <img src={bank.qr} alt={`${bank.name} QR Code`} className="rl-qr-img" />
        </div>
      )}

      {bank.upi && (
        <div className="rl-upi-box">
          <span className="rl-upi-label">UPI ID</span>
          <div className="rl-upi-value">
            <code>{bank.upi}</code>
            <button
              type="button"
              className="rl-copy-btn rl-copy-btn--dark"
              onClick={() => copyText(bank.upi, 'UPI ID')}
              aria-label="Copy UPI ID"
            >
              <Copy size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  </div>
));

const RepayLoan = () => {
  const [mobile, setMobile] = useState('');
  const [customer, setCustomer] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`https://mrrupya.in/Api/TaskApi/oldUserHistory?pancard=${mobile}`);
      const data = await response.json();

      if (data?.listTask?.length > 0) {
        const user = data.listTask[0];
        setCustomer({
          name: user.first_name,
          mobile: user.mobile,
          lead_id: user.lead_id,
          loan_no: user.loan_no,
          pan: user.pancard,
          repayAmount: user.total_repayable,
          tillDateRepayAmount: user.repayment_amount,
          repayDate: user.repayment_date,
          status: user.status,
          email: user.email || 'user@example.com',
          id: user.id || 'CUS',
        });
        setError('');
      } else {
        setCustomer(null);
        setError('Customer not found');
      }
    } catch (err) {
      console.error(err);
      setCustomer(null);
      setError('Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="rl-section" id="repay-loan">
      <div className="rl-bg" aria-hidden="true">
        <div className="rl-bg-gradient" />
        <div className="rl-bg-dots" />
        <span className="rl-bg-orb rl-bg-orb--1" />
        <span className="rl-bg-orb rl-bg-orb--2" />
      </div>

      <div className="rl-wrap">
        <ScrollReveal className="rl-hero" y={28}>
          <span className="rl-eyebrow">Loan repayment</span>
          <h1 className="rl-title">
            Repay your <span>loan easily</span>
          </h1>
          <p className="rl-lead">
            Check your outstanding amount and pay via bank transfer or UPI — quick, secure, and hassle-free.
          </p>
          <ul className="rl-highlights">
            {highlights.map(({ icon: Icon, text }) => (
              <li key={text}>
                <Icon size={16} strokeWidth={2.2} aria-hidden="true" />
                {text}
              </li>
            ))}
          </ul>
        </ScrollReveal>

        <ScrollReveal className="rl-search-card" y={36}>
          <div className="rl-search-head">
            <Building2 size={22} strokeWidth={2} aria-hidden="true" />
            <div>
              <h2>Check repayment details</h2>
              <p>Enter your registered mobile number or PAN to view your loan status.</p>
            </div>
          </div>

          <form onSubmit={handleSearch} className="rl-search-form">
            <div className="rl-search-input-wrap">
              <Search size={18} className="rl-search-icon" aria-hidden="true" />
              <input
                type="text"
                className="rl-search-input"
                placeholder="Mobile number / PAN number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="rl-search-btn" disabled={loading}>
              {loading ? 'Searching…' : 'Check details'}
              {!loading && <ArrowRight size={18} aria-hidden="true" />}
            </button>
          </form>

          {error && (
            <div className="rl-alert rl-alert--error" role="alert">
              {error}
            </div>
          )}
        </ScrollReveal>

        {customer && (
          <ScrollReveal className="rl-result-card" y={32}>
            <div className="rl-result-head">
              <h2>Your loan summary</h2>
            </div>

            <div className="mr-table-wrap">
              <table className="mr-data-table">
                <thead>
                  <tr>
                    <th>Customer Name</th>
                    <th>Mobile Number</th>
                    <th>PAN No</th>
                    <th>Repay Amount</th>
                    <th>Till Date Repay Amount</th>
                    <th>Status</th>
                    <th>Repay Date</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{customer.name}</td>
                    <td>{customer.mobile}</td>
                    <td>{customer.pan}</td>
                    <td className="mr-table-amount">₹{customer.tillDateRepayAmount}</td>
                    <td className="mr-table-amount">₹{customer.repayAmount}</td>
                    <td>
                      <span className="mr-table-status">{customer.status}</span>
                    </td>
                    <td>{customer.repayDate}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </ScrollReveal>
        )}

        <ScrollReveal className="rl-pay-section" y={40}>
          <div className="rl-pay-head">
            <h2>Payment options</h2>
            <p>Transfer to the account below or scan the QR code to pay via UPI.</p>
          </div>

          {banks.map((bank, index) => (
            <BankCard key={index} bank={bank} />
          ))}
        </ScrollReveal>
      </div>
    </section>
  );
};

export default RepayLoan;
